/**
 * Shared page configuration — single source of truth for sidebar + My Pages.
 * Sections map directly to Sidebar category labels.
 */
import {
  LayoutDashboard, Calendar, CheckSquare, MessageSquare, FileText,
  Users, Building2, DollarSign, Package, Video, Film, Image, Target,
  Share2, CalendarDays, Clapperboard, AppWindow, Lock, UtensilsCrossed,
  Home, Music, Activity, ShoppingCart, Boxes, TrendingUp, Disc3, PiggyBank, 
  Briefcase, Headphones, Star, Trophy, MonitorPlay, UserCircle, Radar, 
  Gamepad2, Building, FileStack, BarChart3, HeartPulse, UserCog, Wrench,
  Layout, Monitor, ShieldAlert, Scissors, Brain, Mic, MapPin, Mail, Sparkles
} from 'lucide-react';

export const PAGE_SECTIONS = [
  {
    id: 'workspace',
    label: 'WORKSPACE',
    pages: [
      { icon: LayoutDashboard, label: 'Dashboard',      path: '/',                 alwaysOn: true },
      { icon: Sparkles,        label: 'AI Chat',         path: '/chat',             alwaysOn: true },
      { icon: Monitor,         label: 'Computer Use',    path: '/computer-use',     alwaysOn: true },
      // Counterfind-only: brand-protection review pipeline. The `userOnly`
      // field restricts both the sidebar and the My Pages list to that account.
      { icon: ShieldAlert,     label: 'Product Queue',  path: '/counterfind/product-queue', userOnly: 'counterfind@operatr.com' },
      // Counterfind-only: Mailbox Intelligence for AI-powered email classification
      { icon: Mail,            label: 'Mailbox',        path: '/mailbox', userOnly: 'counterfind@operatr.com' },
      { icon: MessageSquare,   label: 'Communications', path: '/communications-re' },
      { icon: Users,           label: 'Contacts',       path: '/contacts-re' },
      { icon: Calendar,        label: 'Calendar',       path: '/calendar' },
      { icon: CheckSquare,     label: 'Tasks',          path: '/tasks' },
      { icon: Video,           label: 'Projects',       path: '/projects-re' },
    ],
  },
  {
    id: 'lifestyle',
    label: 'LIFESTYLE',
    pages: [
      { icon: MapPin,          label: 'Itineraries',    path: '/itineraries' },
      { icon: Package,         label: 'Travel',         path: '/travel' },
      { icon: Music,           label: 'Tickets',        path: '/tickets' },
      { icon: ShoppingCart,    label: 'Shopping',       path: '/shopping' },
      { icon: UtensilsCrossed, label: 'Food',           path: '/food' },
      { icon: Activity,        label: 'Health',         path: '/health' },
      { icon: Home,            label: 'Smart Home',     path: '/security-cameras' },
    ],
  },
  {
    id: 'files',
    label: 'FILES',
    pages: [
      { icon: FileText,  label: 'Documents',     path: '/documents-re' },
      { icon: Lock,      label: 'Passwords',     path: '/passwords' },
      { icon: Brain,     label: 'Knowledge Base', path: '/knowledge' },
      { icon: Mic,       label: 'Transcriptions', path: '/transcriptions' },
    ],
  },
  {
    id: 'media',
    label: 'MEDIA',
    pages: [
      { icon: Film,        label: 'Media Portfolio', path: '/media-portfolio-re' },
      // Hidden for counterfind — that tenant doesn't need the consumer-style
      // video editor.
      { icon: Scissors,    label: 'Clipping',        path: '/clipping', userExcluded: ['counterfind@operatr.com'] },
      { icon: Clapperboard,label: 'Media Factory',   path: '/media-factory' },
      { icon: Disc3,       label: 'Media Releases',  path: '/music-releases' },
      { icon: Layout,      label: 'Moodboard',       path: '/moodboard' },
      { icon: Monitor,     label: 'Doomscroll',      path: '/doomscroll' },
    ],
  },
  {
    id: 'team',
    label: 'TEAM',
    pages: [
      { icon: Users,      label: 'Clients & Customers', path: '/clients-customers-re' },
      { icon: Users,      label: 'Employees',           path: '/employees-re' },
      { icon: UserCog,    label: 'HR',                  path: '/hr' },
      { icon: Users,      label: 'Talent Database',     path: '/talent-database' },
      { icon: UserCircle, label: 'Rosters',             path: '/rosters' },
    ],
  },
  {
    id: 'operations',
    label: 'OPERATIONS',
    pages: [
      { icon: Package,   label: 'Orders',     path: '/orders' },
      { icon: Calendar,  label: 'Bookings',   path: '/bookings' },
      { icon: Briefcase, label: 'Services',   path: '/creative-services' },
      { icon: Boxes,     label: 'Inventory',  path: '/inventory' },
      { icon: Building2, label: 'Properties', path: '/properties' },
    ],
  },
  {
    id: 'sales',
    label: 'SALES',
    pages: [
      { icon: Target,    label: 'Lead Generation',    path: '/lead-generation' },
      { icon: Headphones, label: 'Customer Service',  path: '/customer-service' },
      { icon: Star,      label: 'Reviews & Feedback', path: '/reviews-feedback' },
    ],
  },
  {
    id: 'marketing',
    label: 'MARKETING',
    pages: [
      { icon: Share2, label: 'Social Media', path: '/social-media' },
      { icon: Share2, label: 'Marketing',    path: '/marketing-re' },
    ],
  },
  {
    id: 'accounting',
    label: 'ACCOUNTING',
    pages: [
      { icon: DollarSign, label: 'Finances',    path: '/finances-re' },
      { icon: PiggyBank,  label: 'Budgeting',   path: '/budgeting' },
      { icon: TrendingUp, label: 'Investments', path: '/investments-re' },
    ],
  },
  {
    id: 'sports',
    label: 'SPORTS',
    pages: [
      { icon: Trophy,     label: 'Live Scores',           path: '/live-scores' },
      { icon: Gamepad2,   label: 'Game Day Operations',   path: '/game-day-operations' },
      { icon: Building,   label: 'Facilities & Equipment', path: '/facilities-equipment' },
      { icon: BarChart3,  label: 'Performance',           path: '/performance' },
      { icon: MonitorPlay,label: 'Film Analysis',         path: '/film-analysis' },
      { icon: HeartPulse, label: 'Player Health',         path: '/player-health' },
      { icon: Radar,      label: 'Scouting',              path: '/scouting' },
      { icon: FileStack,  label: 'Draft',                 path: '/draft' },
    ],
  },
  {
    id: 'always_elevate',
    label: 'ALWAYS ELEVATE',
    pages: [
      // Client pages (akayleb@operatr.com)
      { icon: LayoutDashboard, label: 'Command Center',    path: '/ae/command-center', userOnly: 'akayleb@operatr.com' },
      { icon: Target,          label: 'Brand Blueprint',   path: '/ae/blueprint',      userOnly: 'akayleb@operatr.com' },
      { icon: CheckSquare,     label: 'My Tasks',          path: '/ae/tasks',          userOnly: 'akayleb@operatr.com' },
      { icon: Image,           label: 'Content',           path: '/ae/content',        userOnly: 'akayleb@operatr.com' },
      { icon: Calendar,        label: 'Calendar',          path: '/ae/calendar',       userOnly: 'akayleb@operatr.com' },
      { icon: Briefcase,       label: 'Partnerships',      path: '/ae/partnerships',   userOnly: 'akayleb@operatr.com' },
      { icon: Users,           label: 'Media',             path: '/ae/media',          userOnly: 'akayleb@operatr.com' },
      { icon: Star,            label: 'Community',         path: '/ae/community',      userOnly: 'akayleb@operatr.com' },
      { icon: FileText,        label: 'Assets',            path: '/ae/assets',         userOnly: 'akayleb@operatr.com' },
      { icon: MessageSquare,   label: 'Messages',          path: '/ae/messages',       userOnly: 'akayleb@operatr.com' },
      { icon: Sparkles,        label: 'Notifications',     path: '/ae/notifications',  userOnly: 'akayleb@operatr.com' },
      // Admin pages (elle@operatr.com)
      { icon: LayoutDashboard, label: 'Admin Dashboard',   path: '/ae/admin',          userOnly: 'elle@operatr.com' },
      { icon: Target,          label: 'Account Strategy',  path: '/ae/admin/strategy', userOnly: 'elle@operatr.com' },
      { icon: Video,           label: 'Projects',          path: '/ae/admin/projects', userOnly: 'elle@operatr.com' },
      { icon: CheckSquare,     label: 'Task Management',   path: '/ae/admin/tasks',    userOnly: 'elle@operatr.com' },
      { icon: Film,            label: 'Content Studio',    path: '/ae/admin/content',  userOnly: 'elle@operatr.com' },
      { icon: Briefcase,       label: 'Partnership CRM',   path: '/ae/admin/partnerships', userOnly: 'elle@operatr.com' },
      { icon: Users,           label: 'Media & Visibility', path: '/ae/admin/media',   userOnly: 'elle@operatr.com' },
      { icon: CalendarDays,    label: 'Events',            path: '/ae/admin/events',   userOnly: 'elle@operatr.com' },
      { icon: Star,            label: 'Client Requests',   path: '/ae/admin/requests', userOnly: 'elle@operatr.com' },
      { icon: FileText,        label: 'Asset Library',     path: '/ae/admin/assets',   userOnly: 'elle@operatr.com' },
      { icon: BarChart3,       label: 'Audit Log',         path: '/ae/admin/audit-log', userOnly: 'elle@operatr.com' },
      { icon: BarChart3,       label: 'Reports',           path: '/ae/admin/reports',  userOnly: 'elle@operatr.com' },
    ],
  },
  {
    id: 'utilities',
    label: 'UTILITIES',
    pages: [
      { icon: AppWindow, label: 'Integrations', path: '/my-apps', alwaysOn: true },
    ],
  },
];

/** Flat list of all pages (excluding categories) */
export const ALL_PAGES = PAGE_SECTIONS.flatMap(s => s.pages);
